import { memo, useCallback, useMemo } from 'react'
import { Box, Button, Stack, SvgIcon } from '@mui/material'
import { blue } from '../../../assets/colors'
import FlashIcon from '../../../assets/icons/Flash'
import { Broker_OrderType, OrderType, Product, TransactionType } from '../../../proto/generated/order'
import { TradeOptions } from '../../../reducer/scalper.reducer'
import LiveText from '../../common/LiveText'
import { callNative } from '../../../util/nativeBridge'
import { getChartBidAsk } from '../chartBidAskStore'
import {
    buildChartBidAskSubscribe,
    getChartBidAskSpreadSelector,
    getChartTopAskDisplaySelector,
    getChartTopBidDisplaySelector,
} from './seriesMetadataLiveDisplay'

export type SeriesMetadataBidAskControlsProps = {
    viewKey: string
    priceMultiplier: number
    isEmbedded: boolean
    tradeOptions?: TradeOptions
    quantityInput: React.ReactNode
    onQuickTrade: (
        transaction: TransactionType,
        orderType?: OrderType,
        brokerOrderType?: Broker_OrderType,
        product?: Product,
        price?: number
    ) => void
}

const SeriesMetadataBidAskControls = ({
    viewKey,
    priceMultiplier,
    isEmbedded,
    tradeOptions,
    quantityInput,
    onQuickTrade,
}: SeriesMetadataBidAskControlsProps) => {
    const subscribe = useMemo(() => buildChartBidAskSubscribe(viewKey), [viewKey])
    const topBidSelector = useMemo(() => getChartTopBidDisplaySelector(viewKey, priceMultiplier), [viewKey, priceMultiplier])
    const topAskSelector = useMemo(() => getChartTopAskDisplaySelector(viewKey, priceMultiplier), [viewKey, priceMultiplier])
    const spreadSelector = useMemo(() => getChartBidAskSpreadSelector(viewKey, priceMultiplier), [viewKey, priceMultiplier])

    const flashIcon = tradeOptions?.quickTrade ? (
        <SvgIcon fontSize="small" sx={{ width: '14px', marginRight: '4px' }}>
            <FlashIcon />
        </SvgIcon>
    ) : null

    const handleSellClick = useCallback(() => {
        const { topBid } = getChartBidAsk(viewKey)
        if (isEmbedded) {
            callNative('onChartBidAskClicked', 'false', topBid ? (topBid / priceMultiplier).toString() : '0')
            return
        }
        onQuickTrade(TransactionType.SELL, OrderType.NORMAL, Broker_OrderType.MARKET_ORDER, Product.INTRADAY)
    }, [viewKey, priceMultiplier, isEmbedded, onQuickTrade])

    const handleBuyClick = useCallback(() => {
        const { topAsk } = getChartBidAsk(viewKey)
        if (isEmbedded) {
            callNative('onChartBidAskClicked', 'true', topAsk ? (topAsk / priceMultiplier).toString() : '0')
            return
        }
        onQuickTrade(TransactionType.BUY, OrderType.NORMAL, Broker_OrderType.MARKET_ORDER, Product.INTRADAY)
    }, [viewKey, priceMultiplier, isEmbedded, onQuickTrade])

    return (
        <Stack direction="row" gap={0.5} className="series-meta-container" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
                variant="outlined"
                sx={{
                    py: 0,
                    px: 0.5,
                    minHeight: 24,
                    height: 24,
                    borderColor: (theme) => theme.palette.error.main,
                    background: (theme) => theme.palette.background.paper,
                    color: (theme) => theme.palette.error.main,
                    '&:hover': {
                        borderColor: (theme) => theme.palette.error.main,
                    },
                }}
                onClick={handleSellClick}
            >
                <span style={{ marginRight: 0, lineHeight: '24px', display: 'inline-flex', alignItems: 'center' }}>
                    {flashIcon}
                    <LiveText subscribe={subscribe} selectText={topBidSelector} />
                </span>
            </Button>
            {tradeOptions?.quickTrade ? (
                quantityInput
            ) : (
                <Box component="span" className="series-meta-value" sx={{ margin: '0 5px', lineHeight: '24px' }}>
                    <LiveText subscribe={subscribe} selectText={spreadSelector} />
                </Box>
            )}
            <Button
                variant="outlined"
                sx={{
                    py: 0,
                    px: 0.5,
                    minHeight: 24,
                    height: 24,
                    color: blue,
                    borderColor: blue,
                    background: (theme) => theme.palette.background.paper,
                    '&:hover': {
                        borderColor: blue,
                    },
                }}
                onClick={handleBuyClick}
            >
                <span style={{ marginRight: 0, lineHeight: '24px', display: 'inline-flex', alignItems: 'center', color: blue }}>
                    {flashIcon}
                    <LiveText subscribe={subscribe} selectText={topAskSelector} />
                </span>
            </Button>
        </Stack>
    )
}

export default memo(SeriesMetadataBidAskControls)
